// Client-side fetch for the radiopi /analyze API - a separate service from
// this app's own /api/track-features (see track-features-client.js), full
// audio analysis (tempo/beat grid, loudness, structure segments, drops,
// onsets) rather than the TheAudioDB/Deezer metadata lookup. Called first,
// on track change; main.js falls back to fetchTrackFeatures only when this
// resolves to null (see its own onUpdate for the exact ordering) - "si
// l'API ne renvoie rien, l'orchestration reste comme actuellement".
//
// Goes through this app's own /api/track-analysis proxy (see
// src/routes/api.js + src/services/trackAnalysisService.js), NOT the
// radiopi URL directly: confirmed live that radiopi sends no
// Access-Control-Allow-Origin header at all (its own OPTIONS preflight
// 405s), so a direct browser fetch fails outright with a CORS error
// regardless of which origin party-mode.html is served from. The proxy
// forwards the exact same status code (200/202/4xx/5xx), so the retry
// logic below is unchanged from what it would have been calling radiopi
// directly.
import { API_BASE } from './api-base.js';
const ANALYSIS_URL = API_BASE + '/api/track-analysis';

// A 202 means "queued, come back later" - first-time analysis takes
// ~60-100s per the API's own docs. Poll every 7s; the 18-attempt cap
// (~126s of polling on top of the first request) comfortably covers that
// window without polling forever if the service is genuinely stuck.
const POLL_INTERVAL_MS = 7000;
const MAX_ATTEMPTS = 18;

function requestOnce(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(function (res) {
    return res.json().then(
      function (json) { return { status: res.status, json: json }; },
      function () { return { status: res.status, json: null }; }
    );
  });
}

function delay(ms) {
  return new Promise(function (resolve) { setTimeout(resolve, ms); });
}

// title (required), artist (optional), durationSeconds (required by the
// API - a plain number of seconds is accepted, no need for "mm:ss").
// Never rejects: a definitive error (4xx/5xx), a network failure, or
// exhausting the 202 retry budget all resolve to null so the caller can
// fall back to fetchTrackFeatures unconditionally - "l'API ne bloque
// jamais" is the contract here too.
// onDanceability (optional) is called with radiopi's danceability index off
// EVERY response that carries one - the first 202 included, which is the whole
// point of the field: the client has to pick a visualisation within a second
// of a track starting, and the full analysis takes 60-100s. The estimate
// arrives immediately with source "llm", and the same callback fires again
// with source "signal" once the audio has actually been measured.
//
// Without this the index would be invisible to the client until the analysis
// landed, since only the final 200 body is resolved below.
// options (optional): { url, onState }. `url` swaps the proxy route (the
// /radio page calls /api/webradio-analysis, webradiopi's twin of radiopi);
// `onState` receives the `webradio` field of every response that carries one
// (first-play recording state on the Pi - see webradio.py), so the page's
// debug bar can say "première lecture, enregistrement en cours".
export function fetchTrackAnalysis(title, artist, durationSeconds, onDanceability, options) {
  const opts = options || {};
  const url = opts.url || ANALYSIS_URL;
  const body = { title: title };
  if (artist) body.artist = artist;
  body.duration = durationSeconds;

  function report(json, status) {
    if (onDanceability && json && json.danceability) {
      try {
        onDanceability(json.danceability);
      } catch (err) {
        console.error('[party-mode] danceability callback failed', err);
      }
    }
    if (opts.onState && json && json.webradio) {
      try {
        opts.onState(json.webradio, status);
      } catch (err) {
        console.error('[party-mode] webradio state callback failed', err);
      }
    }
    // CHAQUE RÉPONSE, ENTIÈRE, avec son code HTTP : le panneau technique de
    // judicael.com (ecran-debug.js) en tire le statut de l'analyse — cache,
    // en cours, enregistrement, introuvable — comme /api/profil-musical le
    // donne à halofloor.com.
    if (opts.onResponse) {
      try {
        opts.onResponse(json, status);
      } catch (err) {
        console.error('[party-mode] response callback failed', err);
      }
    }
  }

  function poll(attempt) {
    return requestOnce(url, body).then(
      function (result) {
        report(result.json, result.status);
        if (result.status === 200) return result.json;
        if (result.status === 202 && attempt < MAX_ATTEMPTS) {
          return delay(POLL_INTERVAL_MS).then(function () { return poll(attempt + 1); });
        }
        // 4xx/5xx (definitive - e.g. NOT_FOUND, INVALID_INPUT) or the 202
        // retry budget ran out - not looped on further.
        if (result.status !== 202) {
          console.warn('[party-mode] track analysis unavailable (HTTP ' + result.status + ')', result.json);
        } else {
          console.warn('[party-mode] track analysis still processing after ' + MAX_ATTEMPTS + ' attempts, giving up');
        }
        return null;
      },
      function (err) {
        console.error('[party-mode] track analysis request failed', err);
        return null;
      }
    );
  }

  return poll(1);
}
